import React, { useState, useEffect, startTransition } from 'react';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  alpha,
  Badge
} from '@mui/material';
import {
  Home,
  HomeOutlined,
  Person,
  PersonOutlined,
  Schedule,
  ScheduleOutlined,
  Support,
  SupportOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

// Reusable icon wrapper with subtle animation
const IconWrapper = ({ icon: Icon, selected }) => (
  <div style={{
    width: 25,
    height: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: selected ? 'scale(1.15)' : 'scale(1)',
    transition: 'transform 0.25s ease-out',
  }}>
    <Icon style={{ width: '100%', height: '100%', color: 'inherit' }} />
  </div>
);

const SPStaffBottomNavBar = ({ pendingNotifications = 0 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const theme = useTheme();

  // Sync active tab with route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/sp/staff/appointments')) setValue(1);
    else if (path.includes('/sp/staff/schedule')) setValue(2);
    else if (path.includes('/sp/staff/profile')) setValue(3);
    else if (path.includes('/sp/staff/tickets')) setValue(4);
    else setValue(0);
  }, [location]);

  // Handle navigation with startTransition
  const handleNavigation = (newValue) => {
    setValue(newValue);
    startTransition(() => {
      switch(newValue) {
        case 0:
          navigate('/sp/staff/dashboard');
          break;
        case 1:
          navigate('/sp/staff/appointments');
          break;
        case 2:
          navigate('/sp/staff/schedule');
          break;
        case 3:
          navigate('/sp/staff/profile');
          break;
        case 4:
          navigate('/sp/staff/tickets');
          break;
        default:
          navigate('/sp/staff/dashboard');
      }
    });
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        maxWidth: 800,
        borderRadius: 0,
        background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 1)}, ${alpha(theme.palette.common.white, 1)})`,
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
        overflow: 'hidden',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      elevation={0}
    >
      <BottomNavigation
        value={value}
        onChange={(e, newValue) => handleNavigation(newValue)}
        sx={{
          height: 70,
          padding: '0 10px',
          background: 'transparent',
          '& .MuiBottomNavigationAction-root': {
            color: alpha(theme.palette.primary.main, 2),
            padding: '10px 0',
            transition: 'color 0.3s',
            '&.Mui-selected': {
              color: theme.palette.primary.text,
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              fontWeight: 500,
              transition: 'all 0.3s',
              transform: 'translateY(2px)',
              '&.Mui-selected': {
                fontSize: '0.8rem',
                fontWeight: 600,
                transform: 'translateY(0px)',
              },
            },
          }
        }}
        showLabels
      >
        <BottomNavigationAction
          label="Home"
          icon={<IconWrapper icon={value === 0 ? Home : HomeOutlined} selected={value === 0} />}
        />
        <BottomNavigationAction
          label="Appointments"
          icon={<IconWrapper icon={value === 1 ? Schedule : ScheduleOutlined} selected={value === 1} />}
        />
        <BottomNavigationAction
          label="Schedule"
          icon={<IconWrapper icon={value === 2 ? Schedule : ScheduleOutlined} selected={value === 2} />}
        />
        <BottomNavigationAction
          label="Profile"
          icon={<IconWrapper icon={value === 3 ? Person : PersonOutlined} selected={value === 3} />}
        />
        <BottomNavigationAction
          label="Support"
          icon={
            <Badge badgeContent={pendingNotifications} color="error" max={99}>
              <IconWrapper icon={value === 4 ? Support : SupportOutlined} selected={value === 4} />
            </Badge>
          }
        />
      </BottomNavigation>
    </Paper>
  );
};

export default SPStaffBottomNavBar; 